import { TransactionResponse } from "../managers/signing-manager";
import {
  CreatePasskeySessionRequest,
  CreatePasskeySessionResponse,
  AuthorizePasskeySessionRequest,
  AuthorizePasskeySessionResponse,
  SubmitPasskeySessionRequest,
  SubmitPasskeySessionResponse,
  FindPasskeyAccountRequest,
  FindPasskeyAccountResponse,
  GetPasskeySessionResponse,
  GetPasskeyAccountResponse,
  CreatePasskeyAccountRequest,
  CreatePasskeyAccountResponse,
  TurnkeyInitAuthRequest,
  TurnkeyCompleteAuthRequest,
  ConfirmPayload,
  PrivyInitAuthRequest,
  PrivyCompleteAuthRequest,
  GetPasskeysResponse,
  AddPasskeyRequest,
  AddPasskeyQueryParams,
  AddPasskeyResponse,
  UpdateAccountRequest,
  CreateAccountRequest,
  CreateAccountResponse,
  GetAccountResponse,
  GetAccountBalancesQueryParams,
  GetTransfersOptions,
  TransferResponse,
  CreatePaymentIntentRequest,
  CreatePaymentIntentApiResponse,
  SpendingLimitRequest,
  UpdateSpendingLimitRequest,
  UseSpendingLimitRequest,
  RequestKycLinkRequest,
  KycLink,
  PrepareArbitraryTransactionRequest,
  PrepareArbitraryTransactionQueryParams,
  RemovePasskeyRequest,
  RemovePasskeyQueryParams,
  RemovePasskeyResponse,
  RequestVirtualAccountRequest,
  GetVirtualAccountsQueryParams,
  CreateStandingOrderRequest,
  CreateStandingOrderResponse,
  GetStandingOrdersQueryParams,
  CompleteAuthAndCreateAccountResponse,
  InitAuthResponse,
  TransactionSubmissionResponse,
  GetTransactionsResponse,
  VirtualAccount,
  RefreshSessionRequest,
  RefreshSessionResponse,
  ApiRefreshSessionResponse,
  AuthorizationError,
  GridError,
  AccountBalancesResponse,
  KycStatusResponse,
  KycLinkResponse,
  StandingOrdersResponse,
  StandingOrderResponse,
  VirtualAccountsResponse,
  VirtualAccountResponse,
  CreateSpendingLimitResponse,
  SpendingLimitTransactionResponse,
  SpendingLimitResponse,
  SpendingLimitsResponse,
  // Proposal types
  CreateProposalRequest,
  CreateProposalResponse,
  VoteProposalRequest,
  VoteProposalResponse,
  ExecuteProposalRequest,
  ExecuteProposalResponse,
  // Trade smart transaction types
  CreateTradeSmartTransactionRequest,
  CreateTradeSmartTransactionResponse,
  TradeSmartTransactionQuoteRequest,
  TradeSmartTransactionQuoteResponse,
  PrepareTradeSmartTransactionRequest,
  PrepareTradeSmartTransactionQueryParams,
  PrepareTradeSmartTransactionResponse,
  GetTradeSmartTransactionResponse,
} from "../types";
import { PrivyInternalSignRequest, PrivyCompleteAuthResponse } from "../types/privy";
import { TurnkeyCompleteAuthResponse } from "../types/turnkey";
import { UtilityService } from "../services/utility-service";
import * as uuid from "uuid";

/** Error detail from API validation failures */
export interface GridErrorDetail {
  /** Field that caused the validation error */
  field: string;
  /** Machine-readable error code */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Suggestion for fixing the error */
  suggestion?: string;
  /** Link to relevant documentation */
  documentation?: string;
}

/**
 * Grid API environment: `sandbox` (Devnet) or `production` (Mainnet)
 *
 * @category Configuration
 */
export type GridEnvironment = "sandbox" | "production";

/**
 * Grid API client configuration
 *
 * Security:
 * - `apiKey`: Backend only - never expose in frontend code
 * - `appId`: Safe for frontend - requires domain whitelist configuration
 *
 * @category Configuration
 */
export interface GridApiConfig {
  /** API key for backend authentication (never expose in frontend code) */
  apiKey?: string;
  /** Target environment (sandbox or production) */
  environment: GridEnvironment;
  /** Base URL for the Grid API */
  baseUrl?: string;
  /** Application ID for frontend (safe to expose, whitelist domains in dashboard) */
  appId?: string;
  /** Request timeout in milliseconds */
  timeout?: number;
  /** Retry attempts for failed requests */
  retryAttempts?: number;
  /** Solana RPC endpoint URL */
  solanaRpcUrl?: string;
  /** @internal For testing only - sets Origin header for appId validation */
  __testOrigin?: string;
}

/**
 * @hidden
 * Low-level HTTP client for Grid API
 *
 * Handles authentication, request/response formatting, and error handling.
 * Most users should use GridClient instead.
 *
 * @category API
 */
export class GridApiClient {
  private environment: GridEnvironment;
  private config: GridApiConfig;
  private baseUrl?: string;
  private utilityService: UtilityService;

  constructor(config: GridApiConfig) {
    this.utilityService = new UtilityService();
    // Validate environment if provided
    if (
      config.environment &&
      !["sandbox", "production", "devnet", "mainnet"].includes(config.environment)
    ) {
      throw new Error(
        `Invalid Grid environment: '${config.environment}'. Must be 'sandbox' or 'production'.`
      );
    }

    this.environment = config.environment || "sandbox";
    this.baseUrl = `${config.baseUrl || "https://grid.squads.xyz"}/api/grid/v1`;
    this.config = {
      timeout: 10000,
      retryAttempts: 3,
      ...config,
    };

    // Log warning if no API key is provided
    if (!this.config.apiKey && !this.config.appId) {
      console.warn(
        "⚠️ GridApiClient initialized in Demo Mode."
      );
    }
  }

  private generateIdempotencyKey(): string {
    return uuid.v4();
  }

  /**
   * Check if API key is available
   */
  public hasApiKey(): boolean {
    return Boolean(this.config.apiKey);
  }

  /**
   * @internal
   */
  protected async request<T>(
    endpoint: string,
    options: {
      method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
      data?: any;
      headers?: Record<string, string>;
      skipTransform?: boolean;
      skipTransformResponse?: boolean;
    } = {}
  ): Promise<T> {
    const { method = "GET", data, headers = {}, skipTransform = false, skipTransformResponse = false } = options;

    const url = `${this.baseUrl}${endpoint}`;
    const requestHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      "x-grid-environment": this.environment,
      ...headers,
    };

    // Add idempotency key for POST/PUT/PATCH requests
    if (method === "POST" || method === "PUT" || method === "PATCH") {
      requestHeaders["x-idempotency-key"] = this.generateIdempotencyKey();
    }

    // Add app ID header if provided
    if (this.config.appId) {
      requestHeaders["x-grid-app-id"] = this.config.appId;
    }

    // Only add Authorization header if API key is provided
    if (this.config.apiKey) {
      requestHeaders["Authorization"] = `Bearer ${this.config.apiKey}`;
    }

    // Add Origin header for testing (internal use only)
    if (this.config.__testOrigin) {
      requestHeaders["Origin"] = this.config.__testOrigin;
    }

    try {
      // Transform camelCase request body to snake_case for API (skip for passkey endpoints)
      const requestBody = data 
        ? (skipTransform ? data : this.utilityService.toSnakeCaseObject(data)) 
        : undefined;
      
      const response = await fetch(url, {
        method,
        headers: requestHeaders,
        body: requestBody ? JSON.stringify(requestBody) : undefined,
        credentials: 'include', // Required for HTTP-only cookies to work cross-origin
        // signal: AbortSignal.timeout(this.config.timeout!)
      });

      const requestId = response.headers.get('x-request-id') || '';
      const idempotencyKey = response.headers.get('x-idempotency-key') || undefined;
      const contentType = response.headers.get('content-type') || '';

      // Capture all headers as an object (for both success and error)
      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });

      // Parse response body based on content type
      let result;
      if (contentType.includes('application/json')) {
        result = await response.json();
      } else {
        // Plain text response
        const text = await response.text();
        result = { message: text };
      }

      if (!response.ok) {
        throw new GridError(
          result?.message || "Unknown error",
          response.status.toString(),
          response.status, // status_code
          result?.details, // details from API
          result?.timestamp,
          { requestId, statusCode: response.status, headers } // lastResponse with requestId, statusCode, and headers
        );
      }

      // Extract the actual data (unwrap if API wrapped it in data)
      const responseData = result.data ? result.data : result;

      // Transform snake_case response to camelCase for SDK consumers
      const transformedData = skipTransformResponse ? responseData : this.utilityService.toCamelCaseObject(responseData);

      // Create lastResponse metadata 
      const lastResponse = {
        requestId,
        statusCode: response.status,
        headers,
        ...(idempotencyKey && { idempotencyKey })
      };

      // Always wrap in data + lastResponse pattern
      return {
        data: transformedData,
        lastResponse
      } as T;
    } catch (error) {
      // Re-throw GridError as-is to preserve error codes
      if (error instanceof GridError) {
        throw error;
      }

      // Only network/URL errors reach here (connection failures, etc.)
      throw new GridError(error instanceof Error ? error.message : "Unknown error", "NETWORK_ERROR");
    }
  }

  // Passkey session creation
  async createPasskeySession(
    params: CreatePasskeySessionRequest,
    environment: string
  ): Promise<CreatePasskeySessionResponse> {
    return this.request<CreatePasskeySessionResponse>("/passkeys", {
      method: "POST",
      headers: {
        "x-grid-environment": environment,
      },
      data: params,
      skipTransform: true,
    });
  }

  // Authorize passkey session
  async authorizePasskeySession(
    params: AuthorizePasskeySessionRequest,
    environment: string
  ): Promise<AuthorizePasskeySessionResponse> {
    return this.request<AuthorizePasskeySessionResponse>("/passkeys/auth", {
      method: "POST",
      headers: {
        "x-grid-environment": environment,
      },
      data: params,
      skipTransform: true,
    });
  }

  // Submit passkey session
  async submitPasskeySession(
    params: SubmitPasskeySessionRequest,
    environment: string
  ): Promise<SubmitPasskeySessionResponse> {
    const requestBody = {
      ceremonyType: params.ceremonyType,
      sessionKey: params.sessionKey,
      slotNumber: Number(params.slotNumber),
      authenticatorResponse: params.authenticatorResponse,
    };

    return this.request<SubmitPasskeySessionResponse>("/passkeys/submit", {
      method: "POST",
      headers: {
        "x-grid-environment": environment,
      },
      data: requestBody,
      skipTransform: true,
    });
  }

  // Find passkey account
  async findPasskeyAccount(
    params: FindPasskeyAccountRequest,
    environment: string
  ): Promise<FindPasskeyAccountResponse> {
    return this.request<FindPasskeyAccountResponse>("/passkeys/find", {
      method: "POST",
      headers: {
        "x-grid-environment": environment,
      },
      data: params,
      skipTransform: true,
    });
  }

  // Get passkey account
  // Retrieves passkey account details including relying party ID, public key, and optional session key
  async getPasskeyAccount(
    passkeyAddress: string,
    environment: string
  ): Promise<GetPasskeyAccountResponse> {
    return this.request<GetPasskeyAccountResponse>(`/passkeys/account/${passkeyAddress}`, {
      method: "GET",
      headers: {
        "x-grid-environment": environment,
      },
    });
  }

  // Create passkey account (smart account with passkey signer)
  // Creates a new smart account with the passkey as the initial signer
  async createPasskeyAccount(
    params: CreatePasskeyAccountRequest,
    environment: string
  ): Promise<CreatePasskeyAccountResponse> {
    const response = await this.request<CreatePasskeyAccountResponse>("/passkeys/account", {
      method: "POST",
      headers: {
        "x-grid-environment": environment,
      },
      data: params,
      skipTransform: true,
    });

    return response;
  }

  // Auth endpoints
  async initAuthWithTurnkey(
    request: TurnkeyInitAuthRequest
  ): Promise<InitAuthResponse> {
    // Transform camelCase to snake_case for API
    const apiRequest: Record<string, unknown> = {
      email: request.email,
      // app_name: request.appName,
      // app_icon_url: request.appIconUrl,
      // expiration: request.expiration,
      provider: request.provider,
    };

    if (request.smartAccountAddress) {
      apiRequest.smart_account_address = request.smartAccountAddress;
    }

    return this.request<InitAuthResponse>("/auth", {
      method: "POST",
      data: apiRequest,
    });
  }

  async completeTurnkeyAuth(
    data: TurnkeyCompleteAuthRequest
  ): Promise<TurnkeyCompleteAuthResponse> {
    const apiRequest: Record<string, unknown> = {
      otp_code: data.otpCode,
      kms_provider: "turnkey",
      kms_provider_config: {
        encryption_public_key: data.authPublicKey,
        otp_id: data.otpId,
      },
      email: data.email,
    };

    if (data.smartAccountAddress) {
      apiRequest.smart_account_address = data.smartAccountAddress;
    }

    return this.request<TurnkeyCompleteAuthResponse>("/auth/verify", {
      method: "POST",
      data: apiRequest,
    });
  }

  async completeTurnkeyAuthAndCreateAccount(
    data: TurnkeyCompleteAuthRequest
  ): Promise<CompleteAuthAndCreateAccountResponse> {
    const apiRequest = {
      mpc_primary_id: data.mpcPrimaryId,
      otp_id: data.otpId,
      otp_code: data.otpCode,
      auth_public_key: data.authPublicKey,
      expiration: data.expiration,
    };
    return this.request<CompleteAuthAndCreateAccountResponse>("/auth/complete-and-create-account", {
      method: "POST",
      data: apiRequest,
    });
  }

  // Privy Auth endpoints
  async initAuthWithPrivy(
    request: PrivyInitAuthRequest
  ): Promise<InitAuthResponse> {
    const apiRequest: Record<string, unknown> = {
      email: request.email,
      provider: request.provider || "privy",
      expiration: request.expiration,
    };

    if (request.smartAccountAddress) {
      apiRequest.smart_account_address = request.smartAccountAddress;
    }

    return this.request<InitAuthResponse>("/auth", {
      method: "POST",
      data: apiRequest,
    });
  }

  async completePrivyAuthAndCreateAccount(
    data: PrivyCompleteAuthRequest
  ): Promise<CompleteAuthAndCreateAccountResponse> {
    const apiRequest = {
      email: data.email,
      otp_code: data.otpCode,
      provider: "privy",
      kms_provider_config: {
        encryption_public_key: data.authPublicKey,
      },
      expiration: data.expiration,
    };
    return this.request<CompleteAuthAndCreateAccountResponse>("/accounts/verify", {
      method: "POST",
      data: apiRequest,
    });
  }

  async completePrivyAuth(
    data: PrivyCompleteAuthRequest
  ): Promise<PrivyCompleteAuthResponse> {
    const apiRequest: Record<string, unknown> = {
      email: data.email,
      otp_code: data.otpCode,
      kms_provider: "privy",
      kms_provider_config: {
        encryption_public_key: data.authPublicKey,
      },
      expiration: data.expiration,
    };

    if (data.smartAccountAddress) {
      apiRequest.smart_account_address = data.smartAccountAddress;
    }

    return this.request<PrivyCompleteAuthResponse>("/auth/verify", {
      method: "POST",
      data: apiRequest,
    });
  }

  // Refresh session endpoint
  async refreshSession(
    request: RefreshSessionRequest
  ): Promise<RefreshSessionResponse> {
    const endpoint = "/auth/refresh-session";

    const result = await this.request<ApiRefreshSessionResponse>(endpoint, {
      method: "POST",
      data: {
        kms_payload: request.kmsPayload,
        encryption_public_key: request.encryptionPublicKey,
      },
      skipTransform: true,
      skipTransformResponse: true,
    });

    return {
      data: {
        kmsPayload: result.data.kms_payload,
      },
      lastResponse: result.lastResponse,
    } as RefreshSessionResponse;
  }

  // Transaction endpoints (crypto-native terminology)
  async sendTransaction(
    address: string,
    transactionId: string,
    payload: ConfirmPayload
  ): Promise<TransactionSubmissionResponse> {
    const endpoint = `/accounts/${address}/transactions/${transactionId}/confirm?use-mpc-provider=true`;

    return this.request<TransactionSubmissionResponse>(endpoint, {
      method: "POST",
      data: payload,
    });
  }

  // Submit transaction with KMS payloads format (for Privy)
  async submitPrivyTransaction(
    smartAccountAddress: string,
    submitPayload: any
  ): Promise<TransactionSubmissionResponse> {
    const endpoint = `/accounts/${smartAccountAddress}/submit`;

    return this.request<TransactionSubmissionResponse>(endpoint, {
      method: "POST",
      data: submitPayload,
    });
  }

  // Generic transaction submission
  async submitTransaction(
    smartAccountAddress: string,
    submitPayload: any
  ): Promise<TransactionSubmissionResponse> {
    const endpoint = `/accounts/${smartAccountAddress}/submit`;

    return this.request<TransactionSubmissionResponse>(endpoint, {
      method: "POST",
      data: submitPayload,
      skipTransform: true,
    });
  }

  // Account endpoints
  async getAccount(address: string): Promise<GetAccountResponse> {
    return this.request<GetAccountResponse>(`/accounts/${address}`, {
      method: "GET",
    });
  }

  async getTransactions(address: string): Promise<GetTransactionsResponse> {
    return this.request<GetTransactionsResponse>(`/accounts/${address}/transactions`, {
      method: "GET",
    });
  }

  async getAccountBalances(
    address: string,
    queryParams?: GetAccountBalancesQueryParams
  ): Promise<AccountBalancesResponse> {
    let endpoint = `/accounts/${address}/balances`;

    // Add query parameters if provided
    if (queryParams) {
      const params = new URLSearchParams();
      if (queryParams.limit !== undefined) {
        params.append("limit", queryParams.limit.toString());
      }
      if (queryParams.page !== undefined) {
        params.append("page", queryParams.page.toString());
      }
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
    }

    return this.request<AccountBalancesResponse>(endpoint, {
      method: "GET",
    });
  }

  async getTransfers(
    address: string,
    options?: GetTransfersOptions
  ): Promise<TransferResponse> {
    let endpoint = `/accounts/${address}/transfers`;

    // Add query parameters if provided (camelCase properties → snake_case API params)
    if (options) {
      const params = new URLSearchParams();
      if (options.paymentRail !== undefined) {
        params.append("type", options.paymentRail); // API uses 'type' parameter
      }
      if (options.status !== undefined) {
        params.append("status", options.status);
      }
      if (options.currency !== undefined) {
        params.append("currency", options.currency);
      }
      if (options.startDate !== undefined) {
        params.append("start_date", options.startDate);
      }
      if (options.endDate !== undefined) {
        params.append("end_date", options.endDate);
      }
      if (options.txHash !== undefined) {
        params.append("tx_hash", options.txHash);
      }
      if (options.limit !== undefined) {
        params.append("limit", options.limit.toString());
      }
      if (options.cursor !== undefined) {
        params.append("cursor", options.cursor);
      }
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
    }

    return this.request<TransferResponse>(endpoint, {
      method: "GET",
    });
  }

  // Legacy method for backward compatibility TODO: is this true?
  async getBalance(
    address: string
  ): Promise<AccountBalancesResponse> {
    return this.getAccountBalances(address);
  }

  // Spending limit endpoint
  async createSpendingLimit(
    smartAccountAddress: string,
    request: SpendingLimitRequest
  ): Promise<CreateSpendingLimitResponse> {
    const endpoint = `/accounts/${smartAccountAddress}/spending-limit`;

    return this.request<CreateSpendingLimitResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  // Update spending limit endpoint
  async updateSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string,
    request: UpdateSpendingLimitRequest
  ): Promise<SpendingLimitTransactionResponse> {
    const endpoint = `/accounts/${accountAddress}/spending-limit/${spendingLimitAddress}`;

    return this.request<SpendingLimitTransactionResponse>(endpoint, {
      method: "PATCH",
      data: request,
    });
  }

  // Delete spending limit endpoint
  async deleteSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string
  ): Promise<SpendingLimitTransactionResponse> {
    const endpoint = `/accounts/${accountAddress}/spending-limit/${spendingLimitAddress}`;

    return this.request<SpendingLimitTransactionResponse>(endpoint, {
      method: "DELETE",
      data: {
        transaction_signers: [],
      },
    });
  }

  // Use spending limit endpoint
  async useSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string,
    request: UseSpendingLimitRequest
  ): Promise<SpendingLimitTransactionResponse> {
    const endpoint = `/accounts/${accountAddress}/spending-limit/${spendingLimitAddress}/transactions`;

    return this.request<SpendingLimitTransactionResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  // Get spending limit endpoint
  async getSpendingLimit(
    accountAddress: string,
    spendingLimitAddress: string
  ): Promise<SpendingLimitResponse> {
    const endpoint = `/accounts/${accountAddress}/spending-limit/${spendingLimitAddress}`;

    return this.request<SpendingLimitResponse>(endpoint, {
      method: "GET",
    });
  }

  // Get Spending Limits endpoint
  async getSpendingLimits(
    accountAddress: string
  ): Promise<SpendingLimitsResponse> {
    const endpoint = `/accounts/${accountAddress}/spending-limits`;

    return this.request<SpendingLimitsResponse>(endpoint, {
      method: "GET",
    });
  }

  // Passkey endpoints
  async getPasskeys(
    accountAddress: string
  ): Promise<GetPasskeysResponse> {
    const endpoint = `/accounts/${accountAddress}/passkeys`;

    return this.request<GetPasskeysResponse>(endpoint, {
      method: "GET",
    });
  }

  async addPasskey(
    accountAddress: string,
    request: AddPasskeyRequest,
    queryParams?: AddPasskeyQueryParams
  ): Promise<AddPasskeyResponse> {
    const endpoint = `/accounts/${accountAddress}/passkeys/transaction`;
    const params = queryParams?.admin ? "?admin=true" : "";

    return this.request<AddPasskeyResponse>(endpoint + params, {
      method: "POST",
      data: request,
    });
  }

  async removePasskey(
    accountAddress: string,
    passkeyAddress: string,
    transactionSigners?: string[],
    queryParams?: RemovePasskeyQueryParams
  ): Promise<RemovePasskeyResponse> {
    const endpoint = `/accounts/${accountAddress}/passkeys/${passkeyAddress}/transaction`;
    const params = queryParams?.admin ? "?admin=true" : "";

    return this.request<RemovePasskeyResponse>(endpoint + params, {
      method: "DELETE",
      data: {
        transaction_signers: transactionSigners,
      },
    });
  }

  // Account creation endpoint
  async createAccount(
    request: CreateAccountRequest
  ): Promise<CreateAccountResponse> {
    const endpoint = "/accounts";

    return this.request<CreateAccountResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  // Account update endpoint
  async updateAccount(
    accountAddress: string,
    request: UpdateAccountRequest,
    admin?: boolean,
  ): Promise<TransactionResponse> {
    const endpoint = `/accounts/${accountAddress}`;
    const params = admin ? "?admin=true" : "";

    return this.request<TransactionResponse>(endpoint + params, {
      method: "PATCH",
      data: request,
    });
  }

  // Payment intent endpoint
  async createPaymentIntent(
    accountAddress: string,
    request: CreatePaymentIntentRequest
  ): Promise<CreatePaymentIntentApiResponse> {
    const endpoint = `/accounts/${accountAddress}/payment-intent`;

    return this.request<CreatePaymentIntentApiResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  // Request KYC Link endpoint
  async requestKycLink(
    accountAddress: string,
    request: RequestKycLinkRequest
  ): Promise<KycLinkResponse> {
    const endpoint = `/accounts/${accountAddress}/kyc`;

    return this.request<KycLinkResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  // Get KYC Status endpoint
  async getKycStatus(
    accountAddress: string,
    kycId: string
  ): Promise<KycStatusResponse> {
    const endpoint = `/accounts/${accountAddress}/kyc/${kycId}`;

    return this.request<KycStatusResponse>(endpoint, {
      method: "GET",
    });
  }

  // Prepare Arbitrary Transaction endpoint
  async prepareArbitraryTransaction(
    accountAddress: string,
    request: PrepareArbitraryTransactionRequest,
    queryParams?: PrepareArbitraryTransactionQueryParams
  ): Promise<TransactionResponse> {
    let endpoint = `/accounts/${accountAddress}/transactions`;

    // Add query parameters if provided
    if (queryParams) {
      const params = new URLSearchParams();
      if (queryParams.debug !== undefined) {
        params.append("debug", queryParams.debug.toString());
      }
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
    }

    return this.request<TransactionResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  // ============= Proposal Endpoints =============
  async createProposal(
    accountAddress: string,
    request: CreateProposalRequest
  ): Promise<CreateProposalResponse> {
    const endpoint = `/accounts/${accountAddress}/proposals`;

    return this.request<CreateProposalResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  async voteProposal(
    accountAddress: string,
    proposalAddress: string,
    request: VoteProposalRequest
  ): Promise<VoteProposalResponse> {
    const endpoint = `/accounts/${accountAddress}/proposals/${proposalAddress}/vote`;

    return this.request<VoteProposalResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  async executeProposal(
    accountAddress: string,
    proposalAddress: string,
    request: ExecuteProposalRequest
  ): Promise<ExecuteProposalResponse> {
    const endpoint = `/accounts/${accountAddress}/proposals/${proposalAddress}/execute`;

    return this.request<ExecuteProposalResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  // ============= Trade Smart Transaction Endpoints =============

  /**
   * Create a trade smart transaction policy
   * POST /accounts/{accountAddress}/trade/smart-transactions
   */
  async createTradeSmartTransaction(
    accountAddress: string,
    request: CreateTradeSmartTransactionRequest
  ): Promise<CreateTradeSmartTransactionResponse> {
    const endpoint = `/accounts/${accountAddress}/trade/smart-transactions`;

    return this.request<CreateTradeSmartTransactionResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  /**
   * Get a policy-aware trade quote
   * POST /accounts/{accountAddress}/trade/smart-transactions/{smartTransactionAddress}/quote
   */
  async getTradeSmartTransactionQuote(
    accountAddress: string,
    smartTransactionAddress: string,
    request: TradeSmartTransactionQuoteRequest
  ): Promise<TradeSmartTransactionQuoteResponse> {
    const endpoint = `/accounts/${accountAddress}/trade/smart-transactions/${smartTransactionAddress}/quote`;

    return this.request<TradeSmartTransactionQuoteResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  /**
   * Prepare a trade smart transaction
   * POST /accounts/{accountAddress}/trade/smart-transactions/{smartTransactionAddress}
   */
  async prepareTradeSmartTransaction(
    accountAddress: string,
    smartTransactionAddress: string,
    request: PrepareTradeSmartTransactionRequest,
    queryParams?: PrepareTradeSmartTransactionQueryParams
  ): Promise<PrepareTradeSmartTransactionResponse> {
    let endpoint = `/accounts/${accountAddress}/trade/smart-transactions/${smartTransactionAddress}`;

    // Add query parameters if provided
    if (queryParams) {
      const params = new URLSearchParams();
      if (queryParams.debug !== undefined) {
        params.append("debug", queryParams.debug.toString());
      }
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
    }

    return this.request<PrepareTradeSmartTransactionResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  /**
   * Get a trade smart transaction policy
   * GET /accounts/{accountAddress}/trade/smart-transactions/{smartTransactionAddress}
   */
  async getTradeSmartTransaction(
    accountAddress: string,
    smartTransactionAddress: string
  ): Promise<GetTradeSmartTransactionResponse> {
    const endpoint = `/accounts/${accountAddress}/trade/smart-transactions/${smartTransactionAddress}`;

    return this.request<GetTradeSmartTransactionResponse>(endpoint, {
      method: "GET",
    });
  }

  // Request Virtual Account endpoint
  async requestVirtualAccount(
    accountAddress: string,
    request: RequestVirtualAccountRequest
  ): Promise<VirtualAccountResponse> {
    const endpoint = `/accounts/${accountAddress}/virtual-account`;

    return this.request<VirtualAccountResponse>(endpoint, {
      method: "POST",
      data: request,
    });
  }

  // Get Virtual Accounts endpoint
  async getVirtualAccounts(
    accountAddress: string,
    queryParams?: GetVirtualAccountsQueryParams
  ): Promise<VirtualAccountsResponse> {
    let endpoint = `/accounts/${accountAddress}/virtual-accounts`;

    // Add query parameters if provided (camelCase properties → snake_case API params)
    if (queryParams) {
      const params = new URLSearchParams();
      if (queryParams.sourceCurrency !== undefined) {
        params.append("source_currency", queryParams.sourceCurrency);
      }
      if (queryParams.destinationCurrency !== undefined) {
        params.append("destination_currency", queryParams.destinationCurrency);
      }
      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
    }

    return this.request<VirtualAccountsResponse>(endpoint, {
      method: "GET",
    });
  }

  // Create Standing Order endpoint
  async createStandingOrder(
    accountAddress: string,
    request: CreateStandingOrderRequest
  ): Promise<CreateStandingOrderResponse> {
    const endpoint = `/accounts/${accountAddress}/standing-order-intent`;

    const res = await this.request<CreateStandingOrderResponse>(endpoint, {
      method: "POST",
      data: request,
    });
    return res;
  }

  // Get Standing Orders endpoint
  async getStandingOrders(
    accountAddress: string,
    queryParams?: GetStandingOrdersQueryParams
  ): Promise<StandingOrdersResponse> {
    let endpoint = `/accounts/${accountAddress}/standing-orders`;

    // Add query parameters if provided (camelCase properties → snake_case API params)
    if (queryParams) {
      const params = new URLSearchParams();
      if (queryParams.status !== undefined) {
        params.append("status", queryParams.status);
      }
      if (queryParams.currency !== undefined) {
        params.append("currency", queryParams.currency);
      }
      if (queryParams.startDate !== undefined) {
        params.append("start_date", queryParams.startDate);
      }
      if (queryParams.endDate !== undefined) {
        params.append("end_date", queryParams.endDate);
      }
      if (queryParams.txHash !== undefined) {
        params.append("tx_hash", queryParams.txHash);
      }
      if (queryParams.limit !== undefined) {
        params.append("limit", queryParams.limit.toString());
      }
      if (queryParams.cursor !== undefined) {
        params.append("cursor", queryParams.cursor);
      }

      if (params.toString()) {
        endpoint += `?${params.toString()}`;
      }
    }

    return this.request<StandingOrdersResponse>(endpoint, {
      method: "GET",
    });
  }

  // Get Single Standing Order endpoint
  async getStandingOrder(
    accountAddress: string,
    standingOrderId: string
  ): Promise<StandingOrderResponse> {
    const endpoint = `/accounts/${accountAddress}/standing-order/${standingOrderId}`;

    return this.request<StandingOrderResponse>(endpoint, {
      method: "GET",
    });
  }
}
